import type { GeometryElement as JXGElement } from 'jsxgraph';
import type { BoundLabel } from '@examdojo/core/widget';
import type { EdgePort } from '../../../port';
import type { GeometryPadElementContext, CreatedGeometryElement } from '../../element.model';
import type { LengthAnnotationElement } from './length-annotation.model';
import { LENGTH_ANNOTATION_DEFAULTS } from './length-annotation.model';

/**
 * Create a length annotation: a dimension line with arrows on both ends,
 * offset perpendicular to an edge.
 */
export function createLengthAnnotation(
  element: LengthAnnotationElement & { id: string },
  context: GeometryPadElementContext,
): CreatedGeometryElement {
  const { elementFactory, portRegistry, displayConfig } = context;
  const jsxObjects: JXGElement[] = [];
  const labels: BoundLabel[] = [];

  const edgeGetter = portRegistry.getGetter(element.params.of);
  const offset = element.params.offset ?? LENGTH_ANNOTATION_DEFAULTS.offset;

  // Per-element display overrides
  const elementDisplay = displayConfig.elements?.[element.id];

  // Get the current edge position
  const edge = edgeGetter() as EdgePort;

  // Compute perpendicular offset direction
  const dx = edge.end[0] - edge.start[0];
  const dy = edge.end[1] - edge.start[1];
  const len = Math.sqrt(dx * dx + dy * dy);
  const nx = -dy / len; // perpendicular normal
  const ny = dx / len;

  // Offset points
  const startOffset: [number, number] = [edge.start[0] + nx * offset, edge.start[1] + ny * offset];
  const endOffset: [number, number] = [edge.end[0] + nx * offset, edge.end[1] + ny * offset];

  // Create the dimension line with arrows on both ends
  const lineResult = elementFactory.line({
    a: startOffset,
    b: endOffset,
    name: element.id,
    style: {
      color: elementDisplay?.line?.color ?? 'neutral',
      strokeWidth: elementDisplay?.line?.strokeWidth ?? 1,
      dash: elementDisplay?.line?.dash ?? 2,
      arrows: { first: true, last: true, type: 2, size: 5 },
      visible: elementDisplay?.visible ?? true,
      label: elementDisplay?.line?.label ??
        element.label ?? {
          visible: true,
          contentVariant: 'length',
          variant: 'tertiary',
          color: 'neutral',
        },
      ...(elementDisplay?.line ?? {}),
    },
  });

  jsxObjects.push(lineResult.line, lineResult.a, lineResult.b);
  if (lineResult.lineLabel) {
    labels.push(lineResult.lineLabel);
  }

  // Create thin connection lines from endpoints to actual edge
  const connStart = elementFactory.line({
    a: edge.start,
    b: startOffset,
    style: { color: 'neutral', strokeWidth: 0.5, dash: 1, visible: true, label: false },
  });
  const connEnd = elementFactory.line({
    a: edge.end,
    b: endOffset,
    style: { color: 'neutral', strokeWidth: 0.5, dash: 1, visible: true, label: false },
  });

  jsxObjects.push(connStart.line, connStart.a, connStart.b);
  jsxObjects.push(connEnd.line, connEnd.a, connEnd.b);

  return {
    jsxObjects,
    labels,
    ports: {},
    destroy: () => {
      const board = elementFactory.getContext().board;
      for (const label of labels) {
        board.removeObject(label);
      }
      for (const obj of jsxObjects) {
        board.removeObject(obj);
      }
    },
  };
}
